import React from 'react';
import { OpenSheetMusicDisplay as OSMD, CursorType } from '@museflow-ai/opensheetmusicdisplay';
import ITimeKeeper from 'Models/ITimeKeeper';
import { useSelector } from 'react-redux';
import { LessonReducer } from 'Reducers/lessonReducer';

type CursorProps = {
  timeKeeper: ITimeKeeper;
  startTimestamp: number;
  osmdMeasureDuration: number;
  osmd: OSMD | null;
  isPlaying: boolean;
  countInTimestamp: number;
  cursorIsVisible: boolean;
  measureCount?: number;
};

// Internal POD struct
type CursorOffset = {
  timestamp: number;
  offset: number;
};

export const MuseFlowCursor: React.FC<CursorProps> = ({
  timeKeeper,
  startTimestamp,
  osmdMeasureDuration,
  osmd,
  isPlaying,
  countInTimestamp,
  cursorIsVisible,
  measureCount = 4,
}) => {
  const cursorDiv = React.useRef<HTMLDivElement | null>(null);

  const previousOffset = React.useRef<CursorOffset | null>(null);
  const nextOffset = React.useRef<CursorOffset | null>(null);
  const animationCallbackRef = React.useRef<((ts: DOMHighResTimeStamp) => void) | null>(null);

  const [height, setHeight] = React.useState(0);
  const [offsetTop, setOffsetTop] = React.useState(0);
  const [offsetLeft, setOffsetLeft] = React.useState(0);
  const [opacity, setOpacity] = React.useState('0%');

  const measureStartOffset = React.useRef(0);
  const currMeasureIndex = React.useRef(0);
  const lesson = useSelector((state: LessonReducer) => state.lessonReducer);

  const ensureCursorElement = React.useCallback(() => {
    if (cursorDiv.current) {
      return cursorDiv.current;
    }

    const cursorEl = document.createElement('div');
    cursorEl.id = 'museflow-cursor';
    cursorEl.style.position = 'absolute';
    cursorEl.style.width = '16px';
    cursorEl.style.background = 'rgba(79, 150, 189, 0.26)';
    cursorEl.style.borderRadius = '100px';
    cursorEl.style.transition = 'opacity .25s ease-out';
    cursorDiv.current = cursorEl;
    return cursorEl;
  }, []);

  // separates 32 from "32px" etc.
  const getPixels = (pxString: string): number => {
    return Number.parseFloat(pxString.split('px')[0]);
  };

  const getOffset = React.useCallback((): CursorOffset | null => {
    return osmd?.cursor
      ? {
          timestamp: osmd.cursor.Iterator.currentTimeStamp.RealValue,
          offset: getPixels(osmd.cursor.cursorElement.style.left),
        }
      : null;
  }, [osmd?.cursor]);

  const setAnimationParams = React.useCallback((timestamp: number) => {
    if (nextOffset.current) {
      if (previousOffset.current) {
        // nextOffset.timestamp - previousOffset.timestamp is the normalized [0..1] transition time,
        // e.g. the duration of time between the previous cursor notes and the next cursor notes,
        // relative to the duration of the whole measure.
        const nDistBetween =
          (timestamp - previousOffset.current.timestamp) /
          (nextOffset.current.timestamp - previousOffset.current.timestamp);

        const offset =
          previousOffset.current.offset +
          (nextOffset.current.offset - previousOffset.current.offset) * nDistBetween;

        setOffsetLeft(offset);
      } else {
        // console.log("setting offset", nextOffset.current.offset)
        // If only the next offset is set, we haven't passed the first animation tick yet.
        // Animate immediately.
        setOffsetLeft(nextOffset.current.offset);
      }
    }
  }, []);

  // Finds the OSMD cursor position offsets that the MuseFlow cursor should be between given the current time.
  // If we haven't passed the start time yet, only nextOffset will be set when this function returns.
  const findOffsets = React.useCallback(
    (osmdTime: number): boolean => {
      let offsetsChanged = false;
      if (osmd?.cursor && cursorDiv.current) {
        // Initialize next offset if not already init'd.
        if (!nextOffset.current) {
          osmd.cursor.resetIterator();
          osmd.cursor.show();

          const initialOffset = getOffset();
          currMeasureIndex.current = 0;
          measureStartOffset.current = initialOffset?.offset ?? 0;
          nextOffset.current = initialOffset;
          offsetsChanged = true;

          if (initialOffset) {
            previousOffset.current = {
              timestamp: initialOffset.timestamp - timeKeeper.measureDurationAt(0),
              offset: initialOffset.offset - (countInTimestamp === 0 ? 150 : 0),
            };
          }
        }

        // Advance the timeline until the next offset is ahead of the current time.
        while (
          nextOffset.current &&
          nextOffset.current.timestamp <= osmdTime &&
          !osmd.cursor.Iterator.EndReached
        ) {
          // We've passed the "next" offset. Advance the cursor.
          // The next offset becomes the previous, and we ask OSMD for the next cursor offset now.
          osmd.cursor.next();
          offsetsChanged = true;

          if (!osmd.cursor.Iterator.EndReached) {
            previousOffset.current = nextOffset.current;
            nextOffset.current = getOffset();

            if (currMeasureIndex.current != osmd.cursor.iterator.CurrentMeasureIndex) {
              currMeasureIndex.current = osmd.cursor.iterator.CurrentMeasureIndex;
              measureStartOffset.current = getPixels(osmd.cursor.cursorElement.style.left);
            }
          }
        }

        if (osmd.cursor.Iterator.EndReached && nextOffset.current && previousOffset.current) {
          if (nextOffset.current.timestamp > osmdTime) {
            return offsetsChanged;
          }
          if (nextOffset.current.timestamp === previousOffset.current.timestamp) {
            setOpacity('0%');
            animationCallbackRef.current = null;
            return offsetsChanged;
          }

          // Use our parent element's bounding rect to figure out where the end of the phrase is,
          // since OSMD won't tell us nicely.
          const parentRect = cursorDiv.current.parentElement?.getBoundingClientRect();
          console.assert(parentRect != null);
          if (parentRect == null) {
            return false;
          }

          const finalLength = parentRect.width - parentRect.x;

          previousOffset.current = nextOffset.current;
          nextOffset.current = {
            timestamp: osmdMeasureDuration * measureCount,
            offset: finalLength,
          };

          offsetsChanged = true;
        }
      }
      return offsetsChanged;
    },
    [osmd?.cursor, getOffset, measureCount, timeKeeper, osmdMeasureDuration],
  );

  const animate = React.useCallback(() => {
    if (osmd?.cursor && isPlaying) {
      const cursorEl = ensureCursorElement();
      if (cursorEl.parentElement != osmd.cursor.cursorElement.parentElement) {
        // HACK FIX?? Not sure why we have to do this here...
        // Add our cursor div as a child element of the same div which holds the OSMD cursor
        cursorEl.parentElement?.removeChild(cursorEl);
        osmd.cursor.cursorElement.parentElement?.appendChild(cursorEl);
      }

      // Calculate the time OSMD will give us from the cursor.
      const osmdTime = timeKeeper.audioTimeToMeasureTimestamp() - startTimestamp;
      if (isPlaying && opacity === '0%' && osmdTime >= 0) {
        setOpacity('70%');
      }

      const offsetsChanged = findOffsets(osmdTime);
      if (
        offsetsChanged &&
        nextOffset.current &&
        previousOffset.current &&
        nextOffset.current.timestamp == previousOffset.current.timestamp
      ) {
        animationCallbackRef.current = null;
        if (isPlaying) {
          setOpacity('0%');
        }
      } else if (animationCallbackRef.current) {
        setAnimationParams(osmdTime);
      }
    }
  }, [
    ensureCursorElement,
    osmd?.cursor,
    isPlaying,
    timeKeeper,
    startTimestamp,
    findOffsets,
    setAnimationParams,
  ]);

  React.useEffect(() => {
    if (osmd) {
      const cursorEl = ensureCursorElement();
      // We want the OSMD cursor!
      osmd.setOptions({
        disableCursor: false,
        cursorsOptions: [
          {
            type: CursorType.Standard,
            color: 'green',
            alpha: 0.0,
            follow: true,
          },
        ],
      });
      osmd.cursor.resetIterator();
      osmd.cursor.show();
      // Comment this line out to see the OSMD cursor for debugging
      osmd.cursor.cursorElement.style.opacity = '0%';

      previousOffset.current = null;
      nextOffset.current = null;

      // Add our cursor div as a child element of the same div which holds the OSMD cursor
      cursorEl.parentElement?.removeChild(cursorEl);
      osmd.cursor.cursorElement.parentElement?.appendChild(cursorEl);

      // Set styling for our cursor div.
      // Our cursor is taller than the OSMD cursor, which we correct for in the offsetTop calculation.
      const extraHeight = osmd.cursor.cursorElement.height * 0.3;

      setHeight(osmd.cursor.cursorElement.height + extraHeight);
      setOffsetTop(getPixels(osmd.cursor.cursorElement.style.top) - extraHeight / 2);
      setOffsetLeft(getPixels(osmd.cursor.cursorElement.style.left));
    }
  }, [ensureCursorElement, osmd]);

  React.useEffect(() => {
    nextOffset.current = null;
    previousOffset.current = null;
  }, [startTimestamp]);

  React.useEffect(() => {
    previousOffset.current = null;
    nextOffset.current = null;
    const timestamp = countInTimestamp - startTimestamp;
    if (isPlaying) {
      if (timestamp > countInTimestamp && timestamp < 4) {
        setOpacity('70%');
      }
      animationCallbackRef.current = (_timestamp: DOMHighResTimeStamp) => {
        animate();

        if (animationCallbackRef.current) {
          window.requestAnimationFrame(animationCallbackRef.current);
        }
      };

      window.requestAnimationFrame(animationCallbackRef.current);
    } else {
      animationCallbackRef.current = null;

      findOffsets(timestamp);
      setAnimationParams(timestamp);
      if (timestamp < 0 && startTimestamp != 0) {
        // Delay a frame so that the previously scheduled animation callback runs BEFORE we reset opacity.
        window.requestAnimationFrame(() => setOpacity('0%'));
      } else {
        if (timestamp < 4 && timestamp > 1) {
          setOpacity('70%');
        }
      }
    }
  }, [isPlaying, osmd, animate, countInTimestamp, startTimestamp, findOffsets, setAnimationParams]);

  React.useEffect(() => {
    const timestamp = countInTimestamp - startTimestamp;

    if (!nextOffset.current && isPlaying && timestamp >= countInTimestamp) {
      setOpacity('70%');
    }
  }, [isPlaying, countInTimestamp, startTimestamp]);

  React.useEffect(() => {
    setOpacity('0%');
  }, []);

  React.useEffect(() => {
    setOpacity('0%');
  }, [lesson?.currentUserLevelData?.current_tier]);

  React.useEffect(() => {
    const cursorEl = ensureCursorElement();
    cursorEl.style.height = `${height}px`;
    cursorEl.style.opacity = opacity;
    cursorEl.style.top = `${offsetTop}px`;
    cursorEl.style.left = `${offsetLeft}px`;
    cursorEl.style.visibility = cursorIsVisible ? 'visible' : 'hidden';
  }, [cursorIsVisible, ensureCursorElement, height, offsetLeft, offsetTop, opacity]);

  React.useEffect(() => {
    return () => {
      animationCallbackRef.current = null;
      const cursorEl = cursorDiv.current;
      if (cursorEl?.parentElement) {
        cursorEl.parentElement.removeChild(cursorEl);
      }
      cursorDiv.current = null;
    };
  }, []);

  return null;
};
export default MuseFlowCursor;
